import React, { useMemo } from 'react';

import { InputAdornment } from '@mui/material';

import { CurrencyTableInput } from 'src/app/components/form-elements/inputs/currency-input';
import { TableInput } from 'src/app/components/lib/input';
import { Label } from 'src/app/components/lib/label';
import { MenuItem } from 'src/app/components/lib/menu-item';
import { TableSelect } from 'src/app/components/lib/select/table-select';

import { ThresholdType, thresholdTypes, TotalItemConditionType, totalItemsConditionTypes } from '../../../models';
import {
  getMinItemsPlaceholderFromTotalItemCondition,
  getRequirementsFromRequirementStepState,
} from '../../shared/helpers';
import { InputWithLabelWrapper, RequirementCardRow } from '../../shared/styled';
import { useDiscountWizardStateContext } from '../../state';
import { RequirementsStepActions } from '../../state/requirements';

import { BetweenItemCountWrapper } from './styled';

export function ConditionRow({ requirement }: { requirement: number }) {
  const { requirementsStepDispatch, requirementsStepState } = useDiscountWizardStateContext();
  const { condition, maximumItems, minimumGrams, minimumItems, minimumSpend, totalItemCondition } =
    requirementsStepState[requirement];

  const allRequirements = getRequirementsFromRequirementStepState(requirementsStepState);
  const shouldRemoveNoCondition = Object.keys(allRequirements).length > 1;
  const availableThresholdTypes = shouldRemoveNoCondition
    ? thresholdTypes.filter((type) => type.val !== ThresholdType.NoThreshold)
    : thresholdTypes;

  const minimumItemsPlaceholder = useMemo(
    () => getMinItemsPlaceholderFromTotalItemCondition(totalItemCondition),
    [totalItemCondition]
  );

  return (
    <RequirementCardRow>
      <InputWithLabelWrapper>
        <Label htmlFor='select-input_condition' id='select-label_condition'>
          Condition:
        </Label>
        <TableSelect
          labelForId='condition'
          value={condition}
          width='146px'
          onChange={({ target }) =>
            requirementsStepDispatch({
              type: RequirementsStepActions.SET_REQUIREMENT_CONDITION,
              payload: { value: target.value, index: requirement },
            })
          }
        >
          {availableThresholdTypes.map((type) => (
            <MenuItem key={type.name} value={type.val}>
              {type.name}
            </MenuItem>
          ))}
        </TableSelect>
      </InputWithLabelWrapper>
      {condition === ThresholdType.NumberOfItems && (
        <>
          <InputWithLabelWrapper>
            <Label htmlFor='select-input_apply' id='select-label_apply'>
              Cart contains:
            </Label>
            <TableSelect
              labelForId='apply'
              value={totalItemCondition}
              width='146px'
              onChange={({ target }) =>
                requirementsStepDispatch({
                  type: RequirementsStepActions.SET_REQUIREMENT_TOTAL_ITEM_CONDITION,
                  payload: { value: target.value, index: requirement },
                })
              }
            >
              {totalItemsConditionTypes.map((type) => (
                <MenuItem key={type.name} value={type.val}>
                  {type.name}
                </MenuItem>
              ))}
            </TableSelect>
          </InputWithLabelWrapper>
          <BetweenItemCountWrapper>
            <TableInput
              placeholder={minimumItemsPlaceholder}
              type='number'
              value={minimumItems}
              width='80px'
              onChange={({ target }) =>
                requirementsStepDispatch({
                  type: RequirementsStepActions.SET_REQUIREMENT_MINIMUM_ITEMS,
                  payload: {
                    index: requirement,
                    value: target.value === '' ? '' : Number(target.value),
                  },
                })
              }
            />
            {totalItemCondition === TotalItemConditionType.Between && (
              <>
                -
                <TableInput
                  placeholder='max'
                  type='number'
                  value={maximumItems}
                  width='80px'
                  onChange={({ target }) =>
                    requirementsStepDispatch({
                      type: RequirementsStepActions.SET_REQUIREMENT_MAXIMUM_ITEMS,
                      payload: {
                        index: requirement,
                        value: target.value === '' ? '' : Number(target.value),
                      },
                    })
                  }
                />
              </>
            )}
          </BetweenItemCountWrapper>
        </>
      )}
      {condition === ThresholdType.SpendingAmount && (
        <CurrencyTableInput
          decimalPlaces={2}
          placeholder='Minimum dollar amount'
          value={minimumSpend}
          width='200px'
          onChange={(value) =>
            requirementsStepDispatch({
              type: RequirementsStepActions.SET_REQUIREMENT_MINIMUM_SPEND,
              payload: {
                index: requirement,
                value,
              },
            })
          }
        />
      )}
      {condition === ThresholdType.ProductWeight && (
        <TableInput
          endAdornment={minimumGrams === '' ? undefined : <InputAdornment position='end'>GRAMS</InputAdornment>}
          placeholder='Minimum grams'
          type='number'
          value={minimumGrams}
          width='200px'
          onChange={({ target }) =>
            requirementsStepDispatch({
              type: RequirementsStepActions.SET_REQUIREMENT_MINIMUM_GRAMS,
              payload: {
                index: requirement,
                value: target.value === '' ? '' : Number(target.value),
              },
            })
          }
        />
      )}
    </RequirementCardRow>
  );
}
